import React, { useState, useEffect } from 'react';
import { Box, Text, Stack, Group, ThemeIcon } from '@mantine/core';
import { IconFileSearch } from '@tabler/icons-react';
import { defaultAiRequestManager } from '@/features/ai/components/McpAI/defaultAiRequestManager';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from 'react-i18next';

interface SearchNoteProps {
    onClose?: () => void;
    onSendMessage?: (message: string) => void;
    setInputValue?: (value: string) => void; // TextInput 값을 설정하는 함수
    setPlaceholder?: (placeholder: string) => void; // TextInput 플레이스홀더를 설정하는 함수
}

// 전역 aiManager 인스턴스 사용
const aiManager = defaultAiRequestManager;

const SearchNote: React.FC<SearchNoteProps> = ({ 
    onClose, 
    onSendMessage,
    setInputValue,
    setPlaceholder 
}) => {
    const { user } = useAuthStore();
    const { t } = useTranslation('ai');
    
    // 컴포넌트가 마운트될 때 플레이스홀더 설정
    useEffect(() => {
        // 플레이스홀더 설정 함수가 있는 경우 사용
        if (setPlaceholder) {
            // searchNote 네임스페이스의 placeholder 키 사용
            setPlaceholder(t('features.searchNote.placeholder'));
            console.log('[SearchNote] TextInput 플레이스홀더 설정:', t('features.searchNote.placeholder'));
        } 
        // 플레이스홀더 함수가 없는 경우 기존 방식 사용 (하위 호환성)
        else if (setInputValue) {
            // 텍스트 값을 직접 설정하는 기존 방식 사용
            setInputValue('');
            console.log('[SearchNote] TextInput 값 초기화');
            
            // 직접 플레이스홀더 설정 시도 (상위 컴포넌트에서 이 값을 사용하도록 구현되어 있을 수 있음)
            setTimeout(() => {
                // 다양한 선택자를 사용하여 입력 요소 찾기 시도
                const inputElements = document.querySelectorAll('[data-placeholder]');
                console.log('[SearchNote] 플레이스홀더 요소 검색:', inputElements.length);
                
                if (inputElements.length > 0) {
                    // 모든 플레이스홀더 요소에 적용
                    inputElements.forEach(el => {
                        el.setAttribute('data-placeholder', t('features.searchNote.placeholder'));
                        console.log('[SearchNote] DOM으로 플레이스홀더 직접 설정:', el);
                    });
                }
            }, 10);
        }
        
        // 컴포넌트 마운트 시 웹소켓 연결 확인
        const ensureConnection = async () => {
            try {
                await aiManager.connect();
                console.log('[SearchNote] 웹소켓 연결 확인 완료');
            } catch (error) {
                console.error('[SearchNote] 웹소켓 연결 확인 실패:', error);
            }
        };
        
        void ensureConnection();
    }, [setInputValue, setPlaceholder, t]);
    
    return (
        <Box 
            p="md" 
            style={{ 
                maxWidth: '100%',
                display: 'flex',
                height: '494px',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}
        >
            <Stack align="center" gap="sm" style={{ textAlign: 'center' }}>
                <Group mb="sm">
                    <ThemeIcon 
                        variant="light" 
                        radius="xl" 
                        size={22} 
                        style={{ 
                            backgroundColor: '#E7F5FF',
                            padding: '2px'
                        }}
                    >
                        <IconFileSearch size={20} color="#212529" />
                    </ThemeIcon>
                    <Text fw={600}>{t('features.searchNote.title')}</Text>
                </Group>
                
                <div style={{ marginBottom: '-15px' }}>
                    <Text size="sm" color="dimmed" style={{ textAlign: 'center' }}>
                        {t('features.searchNote.instruction')}
                    </Text>
                </div>
                <div style={{ marginTop: '10px' }}>
                    <Text size="sm" color="dimmed" style={{ textAlign: 'center' }}>
                        {t('features.searchNote.examplePrefix')}<span className="search-note-hashtag">{t('features.searchNote.exampleTag')}</span>
                    </Text>
                </div>
            </Stack>
        </Box>
    );
};

export default SearchNote;
